import React, { useState, useEffect } from 'react';
import { compressFiles, optimizeFormData, validateFormData } from '../../../shared/utils/performanceUtils';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../shared/utils/apiConfig';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate, useParams } from 'react-router-dom';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import 'react-toastify/dist/ReactToastify.css';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaSave, FaImage, FaTimes } from 'react-icons/fa';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PharmacySubCategoriesEdit = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name_en: '',
        name_ar: ''
    });
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [currentImage, setCurrentImage] = useState(null);

    useEffect(() => {
        setTitle('تعديل الفئة الفرعية');
        if (id) {
            fetchSubCategoryDetails();
        }
    }, [id, setTitle]);

    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch (error) {
                return null;
            }
        }
        return null;
    };

    const fetchSubCategoryDetails = async () => {
        try {
            setLoading(true);
            const token = getAuthToken();
            
            if (!token) {
                showErrorToast('لا يوجد token للمصادقة');
                navigate('/pharmacy-subcategories');
                return;
            }

            // جلب الفئة الفرعية المحددة مباشرة بالـ ID
            const response = await axios.get(`${BASE_URL}/pharmacy/sub-categories/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });


            if (response.data.status === 200 || response.data.status === 'success') {
                const subCategory = response.data.data;
                
                if (subCategory) {
                    setFormData({
                        name_en: subCategory.name_en || subCategory.name || '',
                        name_ar: subCategory.name_ar || subCategory.name || ''
                    });
                    if (subCategory.image) {
                        setCurrentImage(subCategory.image);
                        setImagePreview(subCategory.image);
                    }
                } else {
                    showErrorToast('الفئة الفرعية غير موجودة');
                    navigate('/pharmacy-subcategories');
                }
            } else {
                throw new Error(response.data.message || 'فشل في جلب تفاصيل الفئة الفرعية');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب تفاصيل الفئة الفرعية');
            navigate('/pharmacy-subcategories');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showErrorToast('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
                return;
            }
            setImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setImage(null);
        setImagePreview(currentImage);
    };

    const removeCurrentImage = () => {
        setCurrentImage(null);
        setImagePreview(null);
        setImage(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name_en.trim() || !formData.name_ar.trim()) {
            showErrorToast('يرجى ملء جميع الحقول المطلوبة');
            return;
        }

        try {
            setSubmitting(true);
            const token = getAuthToken();

            if (!token) {
                showErrorToast('لا يوجد token للمصادقة');
                return;
            }

            const requestData = new FormData();
            requestData.append('name_en', formData.name_en.trim());
            requestData.append('name_ar', formData.name_ar.trim());
            if (image) {
                requestData.append('image', image);
            }
            if (!currentImage && !image) {
                requestData.append('remove_image', '1');
            }

            const response = await axios.post(`${BASE_URL}/pharmacy/sub-categories/${id}`, requestData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                    'Content-Type': 'multipart/form-data',
                },
            });


            if (response.data.status === 'failed' || response.data.status === 'faild') {
                throw new Error(response.data.message || 'فشل في تعديل الفئة الفرعية');
            }

            showSuccessToast('تم تعديل الفئة الفرعية بنجاح');
            navigate('/pharmacy-subcategories');
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في تعديل الفئة الفرعية');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">جاري التحميل...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary d-flex align-items-center"
                        onClick={() => navigate('/pharmacy-subcategories')}
                        style={{ borderRadius: '8px' }}>
                        <FaArrowLeft className="mx-2" />
                        العودة
                    </motion.button>
                    <div></div>
                </div>

                {/* Edit Form */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="card shadow-sm"
                    style={{ borderRadius: '12px' }}>

                    <div className="card-body p-5">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-6 mb-4">
                                    <label className="form-label fw-medium" style={{ color: '#333', fontSize: '16px' }}>
                                        الاسم بالإنجليزية *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name_en"
                                        value={formData.name_en}
                                        onChange={handleInputChange}
                                        placeholder="أدخل الاسم بالإنجليزية"
                                        required
                                        style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '14px 18px',
                                            fontSize: '15px',
                                            height: '50px'
                                        }}
                                    />
                                </div>
                                <div className="col-md-6 mb-4">
                                    <label className="form-label fw-medium" style={{ color: '#333', fontSize: '16px' }}>
                                        الاسم بالعربية *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name_ar"
                                        value={formData.name_ar}
                                        onChange={handleInputChange}
                                        placeholder="أدخل الاسم بالعربية"
                                        required
                                        style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '14px 18px',
                                            fontSize: '15px',
                                            height: '50px'
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Image Upload Section */}
                            <div className="row mt-4">
                                <div className="col-12 mb-4">
                                    <label className="form-label fw-medium" style={{ color: '#333', fontSize: '16px' }}>
                                        صورة الفئة الفرعية
                                    </label>
                                    <div className="d-flex flex-column gap-3">
                                        {imagePreview ? (
                                            <div className="position-relative" style={{ width: 'fit-content' }}>
                                                <img
                                                    src={imagePreview}
                                                    alt="معاينة الصورة"
                                                    style={{
                                                        width: '200px',
                                                        height: '200px',
                                                        objectFit: 'cover',
                                                        borderRadius: '12px',
                                                        border: '2px solid #e9ecef'
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={image ? removeImage : removeCurrentImage}
                                                    className="btn btn-danger btn-sm position-absolute"
                                                    style={{
                                                        top: '10px',
                                                        right: '10px',
                                                        borderRadius: '50%',
                                                        width: '30px',
                                                        height: '30px',
                                                        padding: '0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        ) : (
                                            <div
                                                className="d-flex flex-column align-items-center justify-content-center"
                                                style={{
                                                    border: '2px dashed #e9ecef',
                                                    borderRadius: '12px',
                                                    padding: '40px',
                                                    backgroundColor: '#f8f9fa',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onClick={() => document.getElementById('imageInput').click()}
                                            >
                                                <FaImage size={48} color="#6c757d" className="mb-3" />
                                                <p className="mb-2 text-muted">انقر لتحميل صورة جديدة</p>
                                                <small className="text-muted">الحد الأقصى 5 ميجابايت</small>
                                            </div>
                                        )}
                                        <input
                                            type="file"
                                            id="imageInput"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            style={{ display: 'none' }}
                                        />
                                        {!imagePreview && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary"
                                                onClick={() => document.getElementById('imageInput').click()}
                                                style={{ borderRadius: '8px', padding: '12px 28px', fontSize: '15px' }}
                                            >
                                                <FaImage className="mx-2" />
                                                اختر صورة
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-center gap-3 mt-5">
                                <motion.button
                                    type="submit"
                                    className="btn btn-primary d-flex align-items-center"
                                    disabled={submitting}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    style={{
                                        backgroundColor: '#184194',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '12px 28px',
                                        fontSize: '15px',
                                        fontWeight: '500'
                                    }}>
                                    <FaSave className="mx-2" />
                                    {submitting ? 'جاري التعديل...' : 'تعديل الفئة الفرعية'}
                                </motion.button>

                                <motion.button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => navigate('/pharmacy-subcategories')}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    style={{
                                        borderRadius: '8px',
                                        padding: '12px 28px',
                                        fontSize: '15px',
                                        fontWeight: '500'
                                    }}>
                                    إلغاء
                                </motion.button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default PharmacySubCategoriesEdit;
